
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Store, LayoutDashboard, Heart, MessageSquare, Briefcase } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import StateCard from '@/components/community/StateCard';
import MarketplaceSection from '@/components/community/MarketplaceSection';
import JobsSection from '@/components/community/JobsSection';
import SinglesForum from '@/components/community/SinglesForum';
import HubSection from '@/components/community/Hub/HubSection';
import AdminDashboard from '@/components/community/admin/AdminDashboard';
import { nigerianStates } from '@/data/nigerianStates';

interface CommunityTabsProps {
  isAdmin: boolean;
  stateAmbassadors: {[key: string]: number};
}

const CommunityTabs = ({ isAdmin, stateAmbassadors }: CommunityTabsProps) => {
  const [activeTab, setActiveTab] = useState('states');
  const [expandedState, setExpandedState] = useState<string | null>(null);
  
  // Handle expanding/collapsing states
  const handleToggleExpand = (state: string) => {
    setExpandedState(expandedState === state ? null : state);
  };
  
  return (
    <Tabs 
      defaultValue="states" 
      value={activeTab}
      onValueChange={(value) => {
        setActiveTab(value);
        setExpandedState(null); // Reset expanded state when changing tabs
      }}
      className="w-full"
    >
      <div className="flex justify-between items-center mb-6">
        <TabsList>
          <TabsTrigger value="states" className="flex items-center">
            <Users size={16} className="mr-2" /> States & LGAs
          </TabsTrigger>
          <TabsTrigger value="hub" className="flex items-center">
            <MessageSquare size={16} className="mr-2" /> Hub
          </TabsTrigger>
          <TabsTrigger value="marketplace" className="flex items-center">
            <Store size={16} className="mr-2" /> Marketplace
          </TabsTrigger>
          <TabsTrigger value="jobs" className="flex items-center">
            <Briefcase size={16} className="mr-2" /> Jobs
          </TabsTrigger>
          <TabsTrigger value="singles" className="flex items-center">
            <Heart size={16} className="mr-2" /> Singles Forum
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admin" className="flex items-center">
              <LayoutDashboard size={16} className="mr-2" /> Admin Dashboard
            </TabsTrigger>
          )}
        </TabsList>
      </div>

      <TabsContent value="states">
        <AnimatePresence>
          <motion.div 
            layout
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            {nigerianStates.map((state) => (
              <StateCard 
                key={state} 
                state={state} 
                isAdmin={isAdmin}
                isExpanded={expandedState === state}
                onToggleExpand={handleToggleExpand}
                ambassadorCount={stateAmbassadors[state] || 0}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </TabsContent>

      <TabsContent value="hub">
        <HubSection isAdmin={isAdmin} />
      </TabsContent>
      
      <TabsContent value="marketplace">
        <MarketplaceSection isAdmin={isAdmin} />
      </TabsContent>
      
      <TabsContent value="jobs">
        <JobsSection isAdmin={isAdmin} />
      </TabsContent>
      
      <TabsContent value="singles">
        <SinglesForum isAdmin={isAdmin} />
      </TabsContent>

      {isAdmin && (
        <TabsContent value="admin">
          <AdminDashboard />
        </TabsContent>
      )}
    </Tabs>
  );
};

export default CommunityTabs;
